/**
 * Shared HTTP helper for Hasura GraphQL POST requests.
 *
 * Built on the browser's native `fetch` — no dependency on
 * `@opengov/compose-app` or `@opengov/app-core/transport`, both of which
 * are being retired from stories-app. New code should call
 * `postHasuraGraphql` instead of inlining fetch boilerplate or reaching
 * for `transport`.
 *
 * Same-origin credentials flow through automatically so the current
 * session cookie is sent, matching what `transport` did for same-origin
 * requests without the dependency.
 */

const HASURA_GRAPHQL_URL = '/api/data/objects/stories/v1/graphql';

export type IHasuraResponse<TData> = {
  data?: TData;
  errors?: { message?: string }[];
};

export async function postHasuraGraphql<TData>(
  query: string,
  variables: Record<string, unknown>
): Promise<TData> {
  const response = await fetch(HASURA_GRAPHQL_URL, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache'
    },
    body: JSON.stringify({ query, variables })
  });

  if (!response.ok) {
    throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
  }

  const json = (await response.json()) as IHasuraResponse<TData>;
  if (json.errors && json.errors.length > 0) {
    throw new Error(json.errors[0]?.message || 'GraphQL error');
  }
  if (!json.data) {
    throw new Error('GraphQL response missing data field');
  }
  return json.data;
}
